import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    IconButton,
    Chip,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    useTheme,
    useMediaQuery,
    Tooltip,
    Pagination,
    Stack
} from '@mui/material';
import {
    ArrowBack,
    Refresh,
    Person,
    Folder,
    Assignment,
    Settings,
    Report,
    Notifications,
    CheckCircle,
    Pending,
    Warning,
    Info,
    Launch,
    PersonAdd,
    TaskAlt,
    Build,
    Assessment,
    School,
    Event,
    Security,
    Code,
    Schedule,
    TrendingUp,
    Error,
    PlayArrow,
    Cancel,
    EditNote,
    GroupWork,
    Work,
    Business,
    Add,
    Update,
    Delete,
    Upload,
    Download,
    Description
} from '@mui/icons-material';
import { useAppTheme } from '@contexts/ThemeContext';
import { databaseService } from '@services/databaseService';

const RecentActivities: React.FC = () => {
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Real data for recent activities from database
    const [activities, setActivities] = useState<any[]>([]);

    const [filter, setFilter] = useState('all');
    const [isLoading, setIsLoading] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10); // Show 10 activities per page

    // Fetch real activities from database
    useEffect(() => {
        const fetchActivities = async () => {
            try {
                console.log('🔄 RecentActivities: Fetching activities...');
                console.log('🔄 RecentActivities: API Base URL:', process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api');
                setIsLoading(true);

                // Test API connectivity first
                try {
                    const testResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api'}/recent-activities`);
                    console.log('🔍 RecentActivities: Test API response status:', testResponse.status);
                    console.log('🔍 RecentActivities: Test API response ok:', testResponse.ok);
                } catch (testError) {
                    console.error('❌ RecentActivities: Test API call failed:', testError);
                }

                const activitiesData = await databaseService.getRecentActivities();

                console.log('📊 RecentActivities: Raw activities data:', activitiesData);
                console.log('📊 RecentActivities: Activities count:', activitiesData?.length || 0);

                // Transform activities to match UI format
                const transformedActivities = activitiesData.map((activity: any) => ({
                    ...activity,
                    timestamp: new Date(activity.timestamp)
                }));

                console.log('📊 RecentActivities: Raw activities data sample:', activitiesData.slice(0, 2));
                console.log('📊 RecentActivities: Transformed activities sample:', transformedActivities.slice(0, 2));
                setActivities(transformedActivities);
            } catch (error) {
                console.error('❌ RecentActivities: Error fetching activities:', error);
                setActivities([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchActivities();
    }, []);

    const getActivityIconColor = (type: string) => {
        switch (type) {
            case 'success': return '#4caf50';
            case 'warning': return '#ff9800';
            case 'error': return '#f44336';
            default: return '#2196f3';
        }
    };

    // Function to get proper message text for status changes
    const getActivityMessage = (activity: any) => {
        const action = activity.action || activity.activityType || 'default';
        const message = activity.message || '';

        console.log('🔍 [DEBUG] getActivityMessage called with:', {
            activityId: activity.id,
            action: action,
            activityType: activity.activityType,
            message: message,
            fullActivity: activity
        });

        // Check if this is a status change activity
        if (action === 'started' || action === 'completed' || action === 'reopened' || action === 'cancelled') {
            // Replace "updated" with specific action in the message
            let newMessage = message;

            if (action === 'started') {
                newMessage = message.replace(/updated/gi, 'started');
                console.log('🔄 [DEBUG] Replacing "updated" with "started"');
            } else if (action === 'completed') {
                newMessage = message.replace(/updated/gi, 'completed');
                console.log('🔄 [DEBUG] Replacing "updated" with "completed"');
            } else if (action === 'reopened') {
                newMessage = message.replace(/updated/gi, 'reopened');
                console.log('🔄 [DEBUG] Replacing "updated" with "reopened"');
            } else if (action === 'cancelled') {
                newMessage = message.replace(/updated/gi, 'cancelled');
                console.log('🔄 [DEBUG] Replacing "updated" with "cancelled"');
            }

            console.log('🔍 [DEBUG] getActivityMessage: Original message:', message);
            console.log('🔍 [DEBUG] getActivityMessage: Action:', action);
            console.log('🔍 [DEBUG] getActivityMessage: New message:', newMessage);
            console.log('🔍 [DEBUG] getActivityMessage: Message changed?', message !== newMessage);

            return newMessage;
        }

        console.log('🔍 [DEBUG] getActivityMessage: Not a status change activity, returning original message');
        return message;
    };

    const getActivityIcon = (activity: any) => {
        // Check action field first for status changes, then activityType
        const action = activity.action || activity.activityType;

        console.log('🎨 getActivityIcon called with:', {
            activityType: activity.activityType,
            action: activity.action,
            finalAction: action,
            message: activity.message
        });

        switch (action) {
            // User activities
            case 'user_registration': return <PersonAdd sx={{ color: '#4caf50', fontSize: '20px' }} />;
            case 'profile_update': return <Person sx={{ color: '#2196f3', fontSize: '20px' }} />;

            // Task activities
            case 'project_completion': return <CheckCircle sx={{ color: '#4caf50', fontSize: '20px' }} />;
            case 'task_assignment': return <Assignment sx={{ color: '#ff9800', fontSize: '20px' }} />;
            case 'task_update': return <EditNote sx={{ color: '#2196f3', fontSize: '20px' }} />;
            case 'task_management': return <TaskAlt sx={{ color: '#9c27b0', fontSize: '20px' }} />;

            // Task status changes
            case 'started': return <PlayArrow sx={{ color: '#ff9800', fontSize: '20px' }} />; // Task started
            case 'completed': return <CheckCircle sx={{ color: '#4caf50', fontSize: '20px' }} />; // Task completed
            case 'reopened': return <Refresh sx={{ color: '#ff5722', fontSize: '20px' }} />; // Task reopened
            case 'cancelled': return <Cancel sx={{ color: '#f44336', fontSize: '20px' }} />; // Task cancelled

            // System activities
            case 'system_maintenance': return <Build sx={{ color: '#607d8b', fontSize: '20px' }} />;
            case 'database_backup': return <Security sx={{ color: '#795548', fontSize: '20px' }} />;
            case 'system_alert': return <Warning sx={{ color: '#f44336', fontSize: '20px' }} />;
            case 'performance_optimization': return <TrendingUp sx={{ color: '#4caf50', fontSize: '20px' }} />;
            case 'security_audit': return <Security sx={{ color: '#9c27b0', fontSize: '20px' }} />;

            // Reports and analytics
            case 'report_generation': return <Assessment sx={{ color: '#3f51b5', fontSize: '20px' }} />;

            // Workshop and session activities
            case 'workshop_completion': return <School sx={{ color: '#4caf50', fontSize: '20px' }} />;
            case 'workshop_scheduled': return <Schedule sx={{ color: '#ff9800', fontSize: '20px' }} />;
            case 'session_start': return <Event sx={{ color: '#2196f3', fontSize: '20px' }} />;
            case 'workshop_tasks': return <GroupWork sx={{ color: '#9c27b0', fontSize: '20px' }} />;
            case 'session_tasks': return <Work sx={{ color: '#3f51b5', fontSize: '20px' }} />;

            // Program management
            case 'program_management': return <Business sx={{ color: '#607d8b', fontSize: '20px' }} />;
            case 'program_created': return <Add sx={{ color: '#4caf50', fontSize: '20px' }} />;
            case 'program_updated': return <Update sx={{ color: '#2196f3', fontSize: '20px' }} />;
            case 'program_deleted': return <Delete sx={{ color: '#f44336', fontSize: '20px' }} />;

            // Feature and development
            case 'feature_deployment': return <Code sx={{ color: '#00bcd4', fontSize: '20px' }} />;
            case 'feature_update': return <Update sx={{ color: '#ff9800', fontSize: '20px' }} />;

            // File and document activities
            case 'file_upload': return <Upload sx={{ color: '#4caf50', fontSize: '20px' }} />;
            case 'file_download': return <Download sx={{ color: '#2196f3', fontSize: '20px' }} />;
            case 'document_created': return <Description sx={{ color: '#9c27b0', fontSize: '20px' }} />;
            case 'document_updated': return <EditNote sx={{ color: '#ff9800', fontSize: '20px' }} />;

            // Default fallback
            default: return <Info sx={{ color: '#757575', fontSize: '20px' }} />;
        }
    };

    // Time-based grouping functions
    const getTimeGroup = (timestamp: Date) => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        const twoDaysAgo = new Date(today.getTime() - 2 * 24 * 60 * 60 * 1000);
        const threeDaysAgo = new Date(today.getTime() - 3 * 24 * 60 * 60 * 1000);
        const activityDate = new Date(timestamp.getFullYear(), timestamp.getMonth(), timestamp.getDate());

        console.log('🕒 Time grouping check:', {
            now: now.toISOString(),
            today: today.toISOString(),
            yesterday: yesterday.toISOString(),
            twoDaysAgo: twoDaysAgo.toISOString(),
            activityDate: activityDate.toISOString(),
            timestamp: timestamp.toISOString()
        });

        if (activityDate.getTime() === today.getTime()) {
            return 'Today';
        } else if (activityDate.getTime() === yesterday.getTime()) {
            return 'Yesterday';
        } else if (activityDate.getTime() === twoDaysAgo.getTime()) {
            return '2 Days Ago';
        } else if (activityDate.getTime() === threeDaysAgo.getTime()) {
            return '3 Days Ago';
        } else if (activityDate.getTime() > threeDaysAgo.getTime()) {
            return 'This Week';
        } else {
            // Show all activities, even older ones
            return 'Older';
        }
    };

    const formatTime = (timestamp: Date) => {
        const now = new Date();
        const diffInMinutes = Math.floor((now.getTime() - timestamp.getTime()) / (1000 * 60));

        console.log('🕒 Format time check:', {
            now: now.toISOString(),
            timestamp: timestamp.toISOString(),
            diffInMinutes: diffInMinutes
        });

        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays === 1) return 'Yesterday';
        if (diffInDays < 7) return `${diffInDays} days ago`;

        const diffInWeeks = Math.floor(diffInDays / 7);
        if (diffInWeeks < 4) return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;

        const diffInMonths = Math.floor(diffInDays / 30);
        return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
    };

    const handleRefresh = () => {
        console.log('🔄 Refresh button clicked - reloading page...');
        // Simply reload the page to get fresh data
        window.location.reload();
    };

    // Handle "Go to Source" navigation based on activity type
    const handleGoToSource = (activity: any) => {
        console.log('🔗 Go to source for activity:', activity);
        console.log('🔗 Activity type:', activity.activityType);
        console.log('🔗 Activity category:', activity.category);
        console.log('🔗 Activity message:', activity.message);
        console.log('🔗 Source ID:', activity.sourceId);
        console.log('🔗 Source Type:', activity.sourceType);
        console.log('🔗 Entity type:', activity.entity_type);
        console.log('🔗 Entity name:', activity.entity_name);

        try {
            // First check category for Workshop Tasks and Session Tasks
            if (activity.category === 'Workshop Planning Tasks' && activity.sourceId) {
                console.log('🔗 Navigating to workshop planning page:', activity.sourceId);
                navigate(`/management-index/workshops/planning?programId=${activity.sourceId}`);
                return;
            } else if (activity.category === 'Workshop Tasks' && activity.sourceId) {
                console.log('🔗 Workshop Tasks detected! sourceId:', activity.sourceId, 'type:', typeof activity.sourceId);
                console.log('🔗 Navigating to workshop planning page:', activity.sourceId);
                navigate(`/management-index/workshops/planning?programId=${activity.sourceId}`);
                return;
            } else if (activity.category === 'Session Tasks' && activity.sourceId) {
                console.log('🔗 Session Tasks detected! sourceId:', activity.sourceId, 'type:', typeof activity.sourceId);
                console.log('🔗 Navigating to session tasks page:', activity.sourceId);
                navigate(`/management-index/sessions/tasks/${activity.sourceId}`);
                return;
            } else if (activity.category === 'Workshop Planning Tasks') {
                console.log('🔗 Workshop Planning Tasks category but no sourceId, going to workshops main page');
                navigate('/workshops');
                return;
            } else if (activity.category === 'Workshop Tasks') {
                console.log('🔗 Workshop Tasks category but no sourceId, going to workshops planning main page');
                console.log('🔗 sourceId value:', activity.sourceId, 'truthy:', !!activity.sourceId);
                navigate('/management-index/workshops/planning');
                return;
            } else if (activity.category === 'Session Tasks') {
                console.log('🔗 Session Tasks category but no sourceId, going to sessions tasks main page');
                console.log('🔗 sourceId value:', activity.sourceId, 'truthy:', !!activity.sourceId);
                navigate('/management-index/sessions/tasks');
                return;
            }

            // Debug: Log if no category matched
            console.log('🔗 No specific category matched, checking sourceType...');

            // Then check sourceType for proper navigation
            if (activity.sourceType) {
                switch (activity.sourceType) {
                    case 'user':
                        // User Management activities
                        navigate('/users');
                        break;

                    case 'session':
                        // Session Management activities - go to sessions main page
                        navigate('/sessions');
                        break;

                    case 'workshop':
                        // Workshop Management activities - go to workshops main page
                        navigate('/workshops');
                        break;

                    case 'task':
                        // Task Management activities - try to determine correct page
                        console.log('🔗 Task sourceType detected, checking for program type...');
                        if (activity.sourceId) {
                            // For now, default to sessions tasks, but this should be improved
                            // based on program type detection
                            navigate(`/management-index/sessions/tasks/${activity.sourceId}`);
                        } else {
                            navigate('/management-index/sessions/tasks');
                        }
                        break;

                    case 'program':
                    default:
                        // Fallback to sessions for program activities
                        navigate('/sessions');
                        break;
                }
            } else if (activity.entity_type) {
                // Fallback to entity_type if sourceType not available
                switch (activity.entity_type) {
                    case 'user':
                        navigate('/users');
                        break;
                    case 'program':
                        navigate('/sessions');
                        break;
                    case 'task':
                        // Default task management navigation
                        console.log('🔗 Task entity_type detected, using default task navigation...');
                        if (activity.sourceId) {
                            // Default task management
                            navigate(`/management-index/sessions/tasks/${activity.sourceId}`);
                        } else {
                            navigate('/management-index/sessions/tasks');
                        }
                        break;
                    default:
                        navigate('/sessions');
                        break;
                }
            } else {
                // Final fallback based on activityType if available
                console.log('🔗 No sourceType or entity_type, checking activityType...');
                if (activity.activityType) {
                    switch (activity.activityType) {
                        case 'user_registration':
                        case 'profile_update':
                        case 'user_role_change':
                        case 'security_update':
                        case 'user_management':
                            navigate('/users');
                            break;

                        case 'project_creation':
                        case 'project_update':
                        case 'project_deletion':
                        case 'project_status_change':
                        case 'project_management':
                        case 'session_start':
                        case 'session_completion':
                        case 'session_update':
                        case 'session_management':
                            navigate('/sessions');
                            break;

                        case 'workshop_scheduled':
                        case 'workshop_update':
                        case 'workshop_completion':
                        case 'workshop_management':
                            navigate('/workshops');
                            break;

                        case 'task_assignment':
                        case 'task_update':
                        case 'task_completion':
                        case 'task_management':
                            // Default task management navigation
                            if (activity.sourceType === 'session') {
                                navigate('/sessions');
                            } else if (activity.sourceType === 'workshop') {
                                navigate('/workshops');
                            } else if (activity.sourceId) {
                                navigate(`/management-index/sessions/tasks/${activity.sourceId}`);
                            } else {
                                navigate('/management-index/sessions/tasks');
                            }
                            break;

                        case 'report_generation':
                            navigate('/reports');
                            break;

                        case 'system_maintenance':
                        case 'database_backup':
                        case 'security_audit':
                        case 'performance_optimization':
                        case 'system_activity':
                            navigate('/dashboard');
                            break;

                        case 'system_alert':
                        case 'error_log':
                            navigate('/notifications');
                            break;

                        default:
                            navigate('/dashboard');
                    }
                } else {
                    // Final fallback
                    console.log('🔗 No activityType available, using final fallback to dashboard');
                    navigate('/dashboard');
                }
            }
        } catch (error) {
            console.error('❌ Error navigating to source:', error);
            console.log('🔗 Error occurred, falling back to dashboard');
            // Fallback to dashboard on error
            navigate('/dashboard');
        }
    };

    // Filter activities based on selected filter
    const filteredActivities = activities.filter((activity: any) => {
        if (filter === 'all') return true;
        return activity.category.toLowerCase() === filter.toLowerCase();
    });

    // Remove duplicate activities based on message, timestamp, and user
    const removeDuplicates = (activities: any[]) => {
        const seen = new Set();
        return activities.filter(activity => {
            // Create a unique key based on message, timestamp, and user
            const key = `${activity.message}-${activity.timestamp?.getTime()}-${activity.user}`;
            if (seen.has(key)) {
                console.log('🔄 Removing duplicate activity:', activity.message);
                return false;
            }
            seen.add(key);
            return true;
        });
    };

    // Apply deduplication to filtered activities
    const deduplicatedActivities = removeDuplicates(filteredActivities);

    // Debug: Log all activities to see what we have
    console.log('🔍 All activities:', activities);
    console.log('🔍 Filtered activities:', filteredActivities);
    console.log('🔍 Deduplicated activities:', deduplicatedActivities);
    console.log('🔍 Activities with task_update:', activities.filter(a => a.activityType === 'task_update'));
    console.log('🔍 Activities with task_management:', activities.filter(a => a.activityType === 'task_management'));
    console.log('🔍 Activities with started action:', activities.filter(a => a.action === 'started'));
    console.log('🔍 Activities with completed action:', activities.filter(a => a.action === 'completed'));
    console.log('🔍 Activities with reopened action:', activities.filter(a => a.action === 'reopened'));

    // Group activities by time (only recent ones)
    const groupedActivities = deduplicatedActivities.reduce((groups: any, activity: any) => {
        const group = getTimeGroup(activity.timestamp);
        console.log('🕒 Grouping activity:', {
            message: activity.message,
            timestamp: activity.timestamp,
            group: group
        });
        if (group && group !== null) {
            if (!groups[group]) {
                groups[group] = [];
            }
            groups[group].push(activity);
        }
        return groups;
    }, {} as Record<string, typeof activities>);

    console.log('📊 Grouped activities:', groupedActivities);
    console.log('📊 Group keys:', Object.keys(groupedActivities));

    // Sort activities within each group by timestamp (newest first)
    Object.keys(groupedActivities).forEach(group => {
        groupedActivities[group].sort((a: any, b: any) => b.timestamp.getTime() - a.timestamp.getTime());
    });

    // Flatten grouped activities for pagination
    const allActivities = Object.keys(groupedActivities).flatMap(group =>
        groupedActivities[group].map((activity: any) => ({ ...activity, group }))
    );

    // Calculate pagination
    const totalPages = Math.ceil(allActivities.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedActivities = allActivities.slice(startIndex, endIndex);

    // Group paginated activities back by time
    const paginatedGroupedActivities = paginatedActivities.reduce((groups: any, activity: any) => {
        if (!groups[activity.group]) {
            groups[activity.group] = [];
        }
        groups[activity.group].push(activity);
        return groups;
    }, {} as Record<string, typeof activities>);

    // Handle page change
    const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
        // Scroll to top when page changes
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Reset to first page when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [filter]);

    const categories = [
        { key: 'all', label: 'All Activities', count: activities.length }
    ];

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 3,
                flexWrap: 'wrap',
                gap: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                        onClick={() => navigate('/dashboard')}
                        sx={{
                            color: isDarkMode ? 'white' : theme.palette.text.primary,
                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                            '&:hover': {
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                            }
                        }}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h4" sx={{
                        color: isDarkMode ? 'white' : theme.palette.text.primary,
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', sm: '2rem' }
                    }}>
                        Recent Activities
                    </Typography>
                </Box>

                <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    sx={{
                        color: isDarkMode ? 'white' : theme.palette.text.primary,
                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                        fontWeight: 500,
                        px: 3,
                        py: 1,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        '&:hover': {
                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.primary.main,
                            transform: 'translateY(-1px)',
                            boxShadow: isDarkMode
                                ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                                : '0 4px 12px rgba(0, 0, 0, 0.1)',
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                        },
                        '&:disabled': {
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.action.disabled,
                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.disabled,
                            transform: 'none',
                            boxShadow: 'none',
                        },
                        transition: 'all 0.2s ease-in-out',
                    }}
                >
                    Refresh
                </Button>
            </Box>

            {/* Filter Chips */}
            <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {categories.map((category) => (
                    <Chip
                        key={category.key}
                        label={`${category.label} (${category.count})`}
                        onClick={() => setFilter(category.key)}
                        variant={filter === category.key ? 'filled' : 'outlined'}
                        sx={{
                            backgroundColor: filter === category.key
                                ? (isDarkMode ? 'rgba(76, 175, 80, 0.2)' : '#4caf50')
                                : 'transparent',
                            color: filter === category.key
                                ? (isDarkMode ? 'white' : 'white')
                                : (isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.primary),
                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                            '&:hover': {
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                            }
                        }}
                    />
                ))}
            </Box>

            {/* Summary Stats */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 2,
                mb: 3
            }}>
                <Card sx={{
                    background: isDarkMode
                        ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)'
                        : 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(76, 175, 80, 0.02) 100%)',
                    border: `1px solid ${isDarkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)'}`,
                    borderRadius: 2
                }}>
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h4" sx={{
                            color: '#4caf50',
                            fontWeight: 700,
                            mb: 1
                        }}>
                            {activities.filter(a => a.type === 'success').length}
                        </Typography>
                        <Typography variant="body2" sx={{
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : theme.palette.text.primary,
                            fontWeight: 500
                        }}>
                            Successful Activities
                        </Typography>
                    </CardContent>
                </Card>

                <Card sx={{
                    background: isDarkMode
                        ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)'
                        : 'linear-gradient(135deg, rgba(33, 150, 243, 0.05) 0%, rgba(33, 150, 243, 0.02) 100%)',
                    border: `1px solid ${isDarkMode ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.2)'}`,
                    borderRadius: 2
                }}>
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h4" sx={{
                            color: '#2196f3',
                            fontWeight: 700,
                            mb: 1
                        }}>
                            {activities.filter(a => a.type === 'info').length}
                        </Typography>
                        <Typography variant="body2" sx={{
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : theme.palette.text.primary,
                            fontWeight: 500
                        }}>
                            Information Updates
                        </Typography>
                    </CardContent>
                </Card>

                <Card sx={{
                    background: isDarkMode
                        ? 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 152, 0, 0.05) 100%)'
                        : 'linear-gradient(135deg, rgba(255, 152, 0, 0.05) 0%, rgba(255, 152, 0, 0.02) 100%)',
                    border: `1px solid ${isDarkMode ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.2)'}`,
                    borderRadius: 2
                }}>
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h4" sx={{
                            color: '#ff9800',
                            fontWeight: 700,
                            mb: 1
                        }}>
                            {activities.filter(a => a.type === 'warning').length}
                        </Typography>
                        <Typography variant="body2" sx={{
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : theme.palette.text.primary,
                            fontWeight: 500
                        }}>
                            Warnings
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Activities List */}
            <Card sx={{
                background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)'
                    : 'linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
                backdropFilter: 'blur(10px)',
                border: isDarkMode
                    ? '1px solid rgba(255, 255, 255, 0.2)'
                    : '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: 3,
                overflow: 'hidden'
            }}>
                <CardContent sx={{ p: 0 }}>
                    {Object.keys(paginatedGroupedActivities).map((groupName) => (
                        <Box key={groupName}>
                            {/* Time Group Header */}
                            <Box sx={{
                                px: { xs: 2, sm: 3 },
                                py: 2,
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                                borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)'
                            }}>
                                <Typography variant="h6" sx={{
                                    color: isDarkMode ? 'white' : theme.palette.text.primary,
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    {groupName}
                                </Typography>
                            </Box>

                            <List sx={{ p: 0 }}>
                                {paginatedGroupedActivities[groupName].map((activity: any, index: number) => (
                                    <React.Fragment key={activity.id}>
                                        <ListItem sx={{
                                            p: { xs: 2, sm: 3 },
                                            '&:hover': {
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                                                transition: 'all 0.2s ease'
                                            }
                                        }}>
                                            <ListItemAvatar>
                                                <Avatar sx={{
                                                    backgroundColor: `${getActivityIconColor(activity.type)}20`,
                                                    color: getActivityIconColor(activity.type),
                                                    border: `2px solid ${getActivityIconColor(activity.type)}30`
                                                }}>
                                                    {getActivityIcon(activity)}
                                                </Avatar>
                                            </ListItemAvatar>

                                            <ListItemText
                                                primary={
                                                    <Box>
                                                        <Typography variant="body1" sx={{
                                                            color: isDarkMode ? 'white' : theme.palette.text.primary,
                                                            fontWeight: 500,
                                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                                            mb: 1
                                                        }}>
                                                            {getActivityMessage(activity)}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                                            <Typography variant="caption" sx={{
                                                                color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
                                                                fontSize: '0.8rem'
                                                            }}>
                                                                {formatTime(activity.timestamp)}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{
                                                                color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : theme.palette.text.secondary,
                                                                fontSize: '0.8rem'
                                                            }}>
                                                                by {activity.user}
                                                            </Typography>
                                                            <Chip
                                                                label={activity.category}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: `${getActivityIconColor(activity.type)}20`,
                                                                    color: getActivityIconColor(activity.type),
                                                                    fontSize: '0.7rem',
                                                                    height: 20
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                }
                                            />

                                            <ListItemSecondaryAction>
                                                    <Tooltip title="Go to Source">
                                                        <IconButton
                                                            onClick={() => {
                                                            console.log('🔍 Full activity object:', activity);
                                                            console.log('🔍 Activity type:', activity.activityType);
                                                            console.log('🔍 Activity category:', activity.category);
                                                            console.log('🔍 Program ID (programId):', activity.programId);
                                                            console.log('🔍 Program ID (program_id):', activity.program_id);
                                                            console.log('🔍 Message:', activity.message);
                                                            console.log('🔍 All activity keys:', Object.keys(activity));

                                                            // Extract program ID from multiple sources
                                                            let programId = activity.programId || activity.program_id;

                                                            // Try to extract from message if not available
                                                            if (!programId && activity.message) {
                                                                // Try different patterns
                                                                const patterns = [
                                                                    /program '([^']+)'/,  // program 'fhel'
                                                                    /program ([0-9]+)/,   // program 285
                                                                    /programId[=:]([0-9]+)/, // programId=285
                                                                    /program_id[=:]([0-9]+)/ // program_id=285
                                                                ];

                                                                for (const pattern of patterns) {
                                                                    const match = activity.message.match(pattern);
                                                                    if (match) {
                                                                        programId = match[1];
                                                                        console.log('🔍 Extracted program ID from message:', programId, 'using pattern:', pattern);
                                                                        break;
                                                                    }
                                                                }
                                                            }

                                                            // Special handling for program name mappings
                                                            if (programId === 'fhel') {
                                                                programId = '285';
                                                                console.log('🔍 Mapped program name "fhel" to program ID 285');
                                                            } else if (programId === 'ASHYert') {
                                                                programId = '267';
                                                                console.log('🔍 Mapped program name "ASHYert" to program ID 267');
                                                            }

                                                            console.log('🔍 Final program ID to use:', programId);

                                                            // Navigate based on activity type and program type
                                                            if (activity.category === 'Session Tasks' || activity.activityType === 'task_management' || activity.activityType === 'task_update') {
                                                                // For session tasks activities
                                                                console.log('🎯 Navigating to Session Tasks for program:', programId);
                                                                if (programId) {
                                                                    // Convert to number if it's a string
                                                                    const numericProgramId = typeof programId === 'string' ? programId : programId.toString();
                                                                    navigate(`/management-index/sessions/tasks/${numericProgramId}`);
                                                                } else {
                                                                    console.log('⚠️ No program ID found, going to sessions list');
                                                                    navigate('/management-index/sessions');
                                                                }
                                                            } else if (activity.category === 'Workshop Tasks' || activity.activityType === 'workshop_tasks') {
                                                                // For workshop tasks activities
                                                                console.log('🎯 Navigating to Workshop Tasks for program:', programId);
                                                                console.log('🎯 Activity category:', activity.category);
                                                                console.log('🎯 Activity type:', activity.activityType);
                                                                if (programId) {
                                                                    // Convert to number if it's a string
                                                                    const numericProgramId = typeof programId === 'string' ? programId : programId.toString();
                                                                    const workshopUrl = `/management-index/workshops/planning?programId=${numericProgramId}`;
                                                                    console.log('🎯 Workshop URL:', workshopUrl);
                                                                    navigate(workshopUrl);
                                                                } else {
                                                                    console.log('⚠️ No program ID found, going to workshops list');
                                                                    navigate('/management-index/workshops');
                                                                }
                                                            } else if (activity.category === 'Workshop Management') {
                                                                // For workshop management activities
                                                                console.log('🎯 Navigating to Workshop Management');
                                                                navigate('/management-index/workshops');
                                                            } else if (activity.category === 'Session Management' || activity.activityType === 'program_management') {
                                                                // For session management activities
                                                                console.log('🎯 Navigating to Session Management');
                                                                navigate('/management-index/sessions');
                                                            } else {
                                                                // Default fallback - check message content
                                                                console.log('🎯 Using fallback navigation logic');
                                                                if (activity.message && activity.message.toLowerCase().includes('workshop')) {
                                                                    console.log('🎯 Detected workshop in message, going to workshops');
                                                                    navigate('/management-index/workshops');
                                                                } else {
                                                                    console.log('🎯 Default to sessions');
                                                                    navigate('/management-index/sessions');
                                                                }
                                                            }
                                                            }}
                                                            sx={{
                                                                color: '#2196f3',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                                                    transform: 'scale(1.1)',
                                                                }
                                                            }}
                                                        >
                                                            <Launch fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                        {index < paginatedGroupedActivities[groupName].length - 1 && (
                                    
                                            <Divider sx={{
                                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                                                mx: { xs: 2, sm: 3 }
                                            }} />
                                        )}
                                    </React.Fragment>
                                ))}
                            </List>
                        </Box>
                    ))}
                </CardContent>
            </Card>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 4,
                    mb: 2
                }}>
                    <Stack spacing={2} alignItems="center">
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            size={isMobile ? "small" : "medium"}
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    color: isDarkMode ? 'white' : theme.palette.text.primary,
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
                                    '&:hover': {
                                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.05)',
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: isDarkMode ? 'rgba(33, 150, 243, 0.3)' : '#2196f3',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: isDarkMode ? 'rgba(33, 150, 243, 0.4)' : '#1976d2',
                                        }
                                    }
                                }
                            }}
                        />
                        <Typography variant="body2" sx={{
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
                            fontSize: '0.875rem'
                        }}>
                            Showing {startIndex + 1}-{Math.min(endIndex, allActivities.length)} of {allActivities.length} activities
                        </Typography>
                    </Stack>
                </Box>
            )}

        </Box>
    );
};

export default RecentActivities;